import { Variants, Transition } from 'framer-motion';

// ============================================
// Spring Presets
// ============================================

export const springs = {
  micro: { type: 'spring', stiffness: 500, damping: 30 } as Transition,
  transition: { type: 'spring', stiffness: 300, damping: 25 } as Transition,
  layout: { type: 'spring', stiffness: 250, damping: 25, mass: 0.8 } as Transition,
  celebration: { type: 'spring', stiffness: 200, damping: 15 } as Transition,
  gentle: { type: 'spring', stiffness: 100, damping: 20 } as Transition,
};

// ============================================
// Page Transitions
// ============================================

export const pageVariants: Variants = {
  initial: { opacity: 0, y: 8 },
  enter: { opacity: 1, y: 0, transition: springs.transition },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
};

export const pageSlideVariants: Variants = {
  initial: (direction: number) => ({
    x: direction > 0 ? 20 : -20,
    opacity: 0,
  }),
  enter: {
    x: 0,
    opacity: 1,
    transition: springs.transition,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -20 : 20,
    opacity: 0,
    transition: { duration: 0.15 },
  }),
};

// ============================================
// Modal & Sheet
// ============================================

export const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: springs.transition,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: { duration: 0.15 },
  },
};

export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export const sheetVariants: Variants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: springs.transition },
  exit: { y: '100%', transition: { duration: 0.2 } },
};

// ============================================
// List & Stagger
// ============================================

export const listContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springs.micro,
  },
};

// ============================================
// Buttons & Interactive
// ============================================

export const buttonTap = { scale: 0.97 };
export const buttonHover = { scale: 1.02 };

export const iconButtonVariants: Variants = {
  rest: { scale: 1, rotate: 0 },
  hover: { scale: 1.1 },
  tap: { scale: 0.9 },
};

// ============================================
// Skeleton & Loading
// ============================================

export const skeletonVariants: Variants = {
  initial: { opacity: 0.4 },
  animate: {
    opacity: [0.4, 0.7, 0.4],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const shimmerVariants: Variants = {
  initial: { x: '-100%' },
  animate: {
    x: '100%',
    transition: {
      duration: 1.2,
      repeat: Infinity,
      ease: 'easeInOut',
      repeatDelay: 0.5,
    },
  },
};

// ============================================
// Chaos System (Revisions)
// ============================================

export const ghostVariants: Variants = {
  initial: { opacity: 0 },
  visible: { opacity: 0.4, transition: springs.gentle },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

export const strikethroughVariants: Variants = {
  hidden: { pathLength: 0 },
  visible: {
    pathLength: 1,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

// ============================================
// Celebrations
// ============================================

export const checkmarkVariants: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.4, ease: 'easeOut' },
      opacity: { duration: 0.1 },
    },
  },
};

export const popVariants: Variants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: springs.celebration,
  },
};

// ============================================
// Accessibility
// ============================================

export const reducedMotionVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: 0.01 } },
  exit: { opacity: 0, transition: { duration: 0.01 } },
};

export const useReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
